<template>
  <div class="min-h-screen flex flex-col">
    <Header v-if="!route.path.startsWith('/admin')" />
    <main class="flex-grow pt-[40px]">
      <router-view v-slot="{ Component, route: routeSlot }">
        <transition name="fade" mode="out-in">
          <component :is="Component" v-if="Component" :key="routeSlot.path" />
        </transition>
      </router-view>
    </main>
    <Footer v-if="!route.path.startsWith('/admin')" />
    <ScrollToTop />
    <CookieConsent />
  </div>
</template>

<script setup>
// Global styles
import './style.css'

// Components
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import ScrollToTop from './components/ScrollToTop.vue'
import CookieConsent from './components/CookieConsent.vue'

// Vue Router
import { useRoute, useRouter } from 'vue-router'

// Vue composables
import { watch, onMounted, nextTick } from 'vue'

// SEO
import { useSEO } from './composables/useSEO'
import { seoKeyMap } from './router'

const route = useRoute()
const router = useRouter()
const { generatePageSEO, updateMetaTags, addStructuredData, generateStructuredData } = useSEO()

// Function to update SEO for current route
const updateSEO = () => {
    // Skip SEO updates for admin routes
    if (route.path.startsWith('/admin')) {
        return
    }

    // Ensure route is ready
    if (!route || !route.name) {
        return
    }

    try {
        const seoKey = seoKeyMap[route.name] || 'home'
        
        // Generate and update SEO data
        const seoData = generatePageSEO(seoKey)
        const structuredData = generateStructuredData(seoKey)
        
        // Update meta tags
        updateMetaTags({
            ...seoData,
            url: window.location.href
        })
        
        // Add structured data
        addStructuredData(structuredData)
    } catch (error) {
        console.warn('SEO update failed:', error)
        // Don't break navigation if SEO fails
    }
}

// Update SEO on route change
watch(() => route.path, async () => {
    // Use nextTick to ensure DOM is ready
    await nextTick()
    updateSEO()
}, { immediate: false })

// Update SEO on initial mount
onMounted(() => {
    updateSEO()
})
</script>

<style>
/* Global styles */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #008067;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #008067;
}

/* Page Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>